import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PhotoUpload from "../components/PhotoUpload";
import VoiceMicButton from "../components/VoiceMicButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  Wrench,
  CheckCircle,
  Clock,
  Package,
  AlertCircle,
  Loader2,
  Download,
  ExternalLink,
  Camera,
  QrCode,
  Copy,
  Phone,
  User,
  CreditCard,
  Truck,
  Timer,
  ThumbsUp,
  Users,
  Send,
  Plus,
  Search,
  Trash2,
  XCircle,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime, getStatusColor, getStatusLabel, PAYMENT_MODES } from "../lib/utils";
import { useCurrency } from "../hooks/useCurrency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status icons mapping
const STATUS_ICONS = {
  received: Clock,
  diagnosed: AlertCircle,
  waiting_for_approval: ThumbsUp,
  in_progress: Wrench,
  pending_parts: Timer,
  repaired: CheckCircle,
  delivered: Truck,
  closed: Package,
  estimate_declined: XCircle,
  returned_without_repair: RotateCcw,
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currencySymbol, formatCurrency } = useCurrency();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [trackingLink, setTrackingLink] = useState(null);

  // Modals
  const [diagnosisModal, setDiagnosisModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState(false);
  const [repairModal, setRepairModal] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(false);
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappData, setWhatsappData] = useState(null);
  const [whatsappType, setWhatsappType] = useState("received");
  const [declineModal, setDeclineModal] = useState(false);
  const [returnWithoutRepairModal, setReturnWithoutRepairModal] = useState(false);

  // Technician notification state
  const [technicianModal, setTechnicianModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);

  // Inventory parts state
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]); // {inventory_id, item_name, quantity, unit_price}
  const [partsSearchQuery, setPartsSearchQuery] = useState("");
  const [showPartsSelector, setShowPartsSelector] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Form states
  const [diagnosisForm, setDiagnosisForm] = useState({
    diagnosis: "",
    estimated_cost: "",
    estimated_timeline: "",
    parts_required: "",
  });

  const [approvalForm, setApprovalForm] = useState({
    approved_by: "",
    approved_amount: "",
    approval_notes: "",
  });

  const [repairForm, setRepairForm] = useState({
    work_done: "",
    parts_replaced: "",
    final_amount: "",
    warranty_info: "",
  });

  const [deliveryForm, setDeliveryForm] = useState({
    delivered_to: "",
    amount_received: "",
    payment_mode: "",
    payment_reference: "",
    delivery_notes: "",
    is_credit: false,
    expense_parts: "",
    expense_labor: "",
  });

  const [declineForm, setDeclineForm] = useState({
    decline_reason: "",
    decline_notes: "",
    other_reason: "",
  });

  const [returnForm, setReturnForm] = useState({
    return_reason: "",
    return_notes: "",
    diagnostic_fee: "",
    fee_received: false,
    other_reason: "",
  });

  useEffect(() => {
    fetchJob();
    fetchTrackingLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);

      // Pre-fill forms if data exists
      if (response.data.diagnosis) {
        setDiagnosisForm({
          diagnosis: response.data.diagnosis.diagnosis || "",
          estimated_cost: response.data.diagnosis.estimated_cost || "",
          estimated_timeline: response.data.diagnosis.estimated_timeline || "",
          parts_required: response.data.diagnosis.parts_required || "",
        });
      }
      if (response.data.approval) {
        setApprovalForm({
          approved_by: response.data.approval.approved_by || "",
          approved_amount: response.data.approval.approved_amount || "",
          approval_notes: response.data.approval.approval_notes || "",
        });
      }
      if (response.data.repair) {
        setRepairForm({
          work_done: response.data.repair.work_done || "",
          parts_replaced: response.data.repair.parts_replaced || "",
          final_amount: response.data.repair.final_amount || "",
          warranty_info: response.data.repair.warranty_info || "",
        });
        // Pre-fill selected parts if previously saved
        if (response.data.repair.parts_used?.length > 0) {
          setSelectedParts(response.data.repair.parts_used.map(p => ({
            inventory_id: p.inventory_id,
            item_name: p.item_name,
            quantity: p.quantity,
            unit_price: p.unit_price || 0
          })));
        }
      }
      if (response.data.delivery) {
        setDeliveryForm({
          delivered_to: response.data.delivery.delivered_to || "",
          amount_received: response.data.delivery.amount_received || "",
          payment_mode: response.data.delivery.payment_mode || "",
          payment_reference: response.data.delivery.payment_reference || "",
          delivery_notes: response.data.delivery.delivery_notes || "",
        });
      }
    } catch (error) {
      toast.error("Failed to load job details");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async (searchQuery = "") => {
    setLoadingInventory(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      const response = await axios.get(`${API}/inventory?${params.toString()}`);
      setInventoryItems(response.data);
    } catch (error) {
      console.error("Failed to load inventory:", error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleAddPart = (item) => {
    // Check if already added
    const existing = selectedParts.find(p => p.inventory_id === item.id);
    if (existing) {
      toast.error("Part already added");
      return;
    }
    if (item.quantity < 1) {
      toast.error("Part is out of stock");
      return;
    }
    setSelectedParts([...selectedParts, {
      inventory_id: item.id,
      item_name: item.name,
      quantity: 1,
      unit_price: item.selling_price || 0,
      available_stock: item.quantity
    }]);
    setShowPartsSelector(false);
    setPartsSearchQuery("");
  };

  const handleRemovePart = (inventoryId) => {
    setSelectedParts(selectedParts.filter(p => p.inventory_id !== inventoryId));
  };

  const handlePartQuantityChange = (inventoryId, newQuantity) => {
    setSelectedParts(selectedParts.map(p => {
      if (p.inventory_id === inventoryId) {
        const qty = Math.max(1, Math.min(newQuantity, p.available_stock || 999));
        return { ...p, quantity: qty };
      }
      return p;
    }));
  };

  const fetchTrackingLink = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}/tracking-link`);
      setTrackingLink(response.data);
    } catch (error) {
      console.error("Failed to fetch tracking link");
    }
  };

  const handlePhotoChange = (newPhotos) => {
    setJob((prev) => ({ ...prev, photos: newPhotos }));
  };

  const copyTrackingLink = () => {
    if (trackingLink) {
      const fullUrl = `${window.location.origin}${trackingLink.tracking_path}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success("Tracking link copied!");
    }
  };

  // Diagnosis
  const handleDiagnosis = async (sendWhatsApp = false) => {
    if (!diagnosisForm.diagnosis || !diagnosisForm.estimated_cost || !diagnosisForm.estimated_timeline) {
      toast.error("Please fill all required fields");
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API}/jobs/${id}/diagnosis`, diagnosisForm);
      toast.success("Diagnosis added");
      setDiagnosisModal(false);
      await fetchJob();
      if (sendWhatsApp) {
        openWhatsApp("diagnosis");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add diagnosis");
    } finally {
      setActionLoading(false);
    }
  };

  // Approval
  const handleApproval = async (sendWhatsApp = false) => {
    if (!approvalForm.approved_by || !approvalForm.approved_amount) {
      toast.error("Please fill required fields");
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API}/jobs/${id}/approve`, approvalForm);
      toast.success("Approval recorded");
      setApprovalModal(false);
      await fetchJob();
      if (sendWhatsApp) {
        openWhatsApp("approved");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record approval");
    } finally {
      setActionLoading(false);
    }
  };

  // Pending Parts
  const handlePendingParts = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/jobs/${id}/pending-parts?notes=Waiting for parts to arrive`);
      toast.success("Marked as pending parts");
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };

  // Repair
  const handleRepair = async (sendWhatsApp = false) => {
    if (!repairForm.work_done || !repairForm.final_amount) {
      toast.error("Please fill required fields");
      return;
    }
    setActionLoading(true);
    try {
      // Build payload with parts_used from inventory
      const payload = {
        ...repairForm,
        final_amount: parseFloat(repairForm.final_amount),
        parts_used: selectedParts.length > 0 ? selectedParts.map(p => ({
          inventory_id: p.inventory_id,
          item_name: p.item_name,
          quantity: p.quantity,
          unit_price: p.unit_price
        })) : null
      };
      await axios.put(`${API}/jobs/${id}/repair`, payload);
      toast.success("Repair completed");
      setRepairModal(false);
      await fetchJob();
      if (sendWhatsApp) {
        openWhatsApp("repaired");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update repair");
    } finally {
      setActionLoading(false);
    }
  };

  // Delivery
  const handleDelivery = async (sendWhatsApp = false) => {
    if (!deliveryForm.delivered_to || !deliveryForm.amount_received || !deliveryForm.payment_mode) {
      toast.error("Please fill required fields");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        ...deliveryForm,
        amount_received: parseFloat(deliveryForm.amount_received) || 0,
        expense_parts: deliveryForm.expense_parts ? parseFloat(deliveryForm.expense_parts) : null,
        expense_labor: deliveryForm.expense_labor ? parseFloat(deliveryForm.expense_labor) : null,
      };
      await axios.put(`${API}/jobs/${id}/deliver`, payload);
      toast.success("Delivery recorded");
      setDeliveryModal(false);
      await fetchJob();
      if (sendWhatsApp) {
        openWhatsApp("delivered");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record delivery");
    } finally {
      setActionLoading(false);
    }
  };

  // Close Job
  const handleClose = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/jobs/${id}/close`);
      toast.success("Job closed");
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to close job");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Estimate Declined
  const handleDecline = async () => {
    if (!declineForm.decline_reason) {
      toast.error("Please provide a reason for decline");
      return;
    }
    if (declineForm.decline_reason === "other" && !declineForm.other_reason?.trim()) {
      toast.error("Please specify the reason for decline");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        ...declineForm,
        decline_reason: declineForm.decline_reason === "other" 
          ? `Other: ${declineForm.other_reason}` 
          : declineForm.decline_reason
      };
      await axios.put(`${API}/jobs/${id}/decline`, payload);
      toast.success("Job marked as estimate declined");
      setDeclineModal(false);
      setDeclineForm({ decline_reason: "", decline_notes: "", other_reason: "" });
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Return Without Repair
  const handleReturnWithoutRepair = async () => {
    if (!returnForm.return_reason) {
      toast.error("Please provide a reason for return");
      return;
    }
    if (returnForm.return_reason === "other" && !returnForm.other_reason?.trim()) {
      toast.error("Please specify the reason for return");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        ...returnForm,
        return_reason: returnForm.return_reason === "other" 
          ? `Other: ${returnForm.other_reason}` 
          : returnForm.return_reason
      };
      await axios.put(`${API}/jobs/${id}/return-without-repair`, payload);
      toast.success("Job marked as returned without repair");
      setReturnWithoutRepairModal(false);
      setReturnForm({ return_reason: "", return_notes: "", diagnostic_fee: "", fee_received: false, other_reason: "" });
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    } finally {
      setActionLoading(false);
    }
  };

  // Technician notification functions
  const fetchTechnicians = async () => {
    setLoadingTechnicians(true);
    try {
      const response = await axios.get(`${API}/users`);
      const techList = response.data.filter(u => u.role === "technician" || u.role === "admin");
      setTechnicians(techList);
    } catch (error) {
      console.error("Failed to fetch technicians:", error);
      toast.error("Failed to load team members");
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const handleOpenTechnicianModal = () => {
    setTechnicianModal(true);
    setSelectedTechnician(null);
    fetchTechnicians();
  };

  const generateTechnicianMessage = () => {
    if (!job) return "";
    
    const accessories = job.accessories?.filter(a => a.checked).map(a => a.name).join(", ") || "None";
    const status = getStatusLabel(job.status);
    
    // Message WITHOUT customer personal details
    let message = `🔧 *Job Update - ${job.job_number}*

*Status:* ${status}

*Device Details:*
• Type: ${job.device?.device_type || "N/A"}
• Brand: ${job.device?.brand || "N/A"}
• Model: ${job.device?.model || "N/A"}
${job.device?.serial_imei ? `• IMEI/Serial: ${job.device.serial_imei}` : ""}
${job.device?.condition ? `• Condition: ${job.device.condition}` : ""}

*Problem:*
${job.problem_description || "N/A"}

*Accessories:*
${accessories}`;

    if (job.device?.password) {
      message += `\n\n*Device Password:* ${job.device.password}`;
    }
    if (job.device?.unlock_pattern) {
      message += `\n*Unlock Pattern:* ${job.device.unlock_pattern}`;
    }

    if (job.diagnosis) {
      message += `\n\n*Diagnosis:*
${job.diagnosis.diagnosis}
Est. Cost: ${formatCurrency(job.diagnosis.estimated_cost || 0)}`;
    }

    message += `\n\nPlease check and update the status.`;

    return message;
  };

  const handleSendToTechnician = () => {
    if (!selectedTechnician) {
      toast.error("Please select a technician");
      return;
    }

    const tech = technicians.find(t => t.id === selectedTechnician);
    if (!tech?.phone) {
      toast.error("Technician phone number not available");
      return;
    }

    const message = generateTechnicianMessage();
    const encodedMessage = encodeURIComponent(message);
    let phone = tech.phone.replace(/[^0-9]/g, "");
    // Handle local format starting with 0
    if (phone.startsWith("0") && phone.length === 11) {
      phone = "91" + phone.slice(1);
    } else if (!phone.startsWith("91") && phone.length === 10) {
      phone = "91" + phone;
    }
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, "_blank");
    toast.success(`WhatsApp opened for ${tech.name}`);
    setTechnicianModal(false);
  };

  // WhatsApp
  const openWhatsApp = async (messageType) => {
    try {
      const response = await axios.get(`${API}/jobs/${id}/whatsapp-message?message_type=${messageType}`);
      setWhatsappData(response.data);
      setWhatsappType(messageType);
      setWhatsappModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate message");
    }
  };

  // PDF
  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `job-${job.job_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!job) return null;

  const StatusIcon = STATUS_ICONS[job.status] || Clock;

  // Determine which actions are available based on status
  const canDiagnose = job.status === "received";
  const canApprove = job.status === "diagnosed" || job.status === "waiting_for_approval";
  const canDecline = job.status === "diagnosed" || job.status === "waiting_for_approval";
  const canMarkPendingParts = job.status === "in_progress";
  const canRepair = job.status === "in_progress" || job.status === "pending_parts";
  const canReturnWithoutRepair = job.status === "in_progress" || job.status === "pending_parts";
  const canDeliver = job.status === "repaired";
  const canClose = job.status === "delivered";

  return (
    <Layout>
      <div className="space-y-6 animate-in" data-testid="job-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/jobs")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-mono">{job.job_number}</h1>
                <Badge className={`${getStatusColor(job.status)} text-sm`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {getStatusLabel(job.status)}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                Created {formatDateTime(job.created_at)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button 
              size="sm" 
              onClick={() => openWhatsApp(job.status)} 
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="whatsapp-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              WhatsApp Customer
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer & Device */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Customer & Device</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Customer</h4>
                    <p className="font-semibold">{job.customer.name}</p>
                    <p className="text-sm">{job.customer.mobile}</p>
                    {job.customer.email && (
                      <p className="text-sm text-muted-foreground">{job.customer.email}</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Device</h4>
                    <p className="font-semibold">{job.device.brand} {job.device.model}</p>
                    <p className="text-sm font-mono">{job.device.serial_imei}</p>
                    <p className="text-sm">{job.device.device_type} • {job.device.condition}</p>
                    {job.device.condition_notes && (
                      <p className="text-sm text-muted-foreground">{job.device.condition_notes}</p>
                    )}
                  </div>
                </div>
                {/* Device Password, Unlock Pattern and Notes */}
                {(job.device.password || job.device.unlock_pattern || job.device.notes) && (
                  <div className="grid sm:grid-cols-3 gap-6 pt-4 border-t">
                    {job.device.password && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Device Password</h4>
                        <p className="font-mono bg-muted px-2 py-1 rounded inline-block">{job.device.password}</p>
                      </div>
                    )}
                    {job.device.unlock_pattern && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Unlock Pattern</h4>
                        <p className="font-mono bg-muted px-2 py-1 rounded inline-block">{job.device.unlock_pattern}</p>
                      </div>
                    )}
                    {job.device.notes && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Device Notes</h4>
                        <p className="text-sm">{job.device.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Problem */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Problem Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Customer-Reported Issue</h4>
                  <p>{job.problem_description}</p>
                </div>
                {job.technician_observation && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Technician Observation</h4>
                    <p>{job.technician_observation}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Device Photos */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Device Photos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PhotoUpload jobId={id} photos={job.photos || []} onPhotoChange={handlePhotoChange} />
              </CardContent>
            </Card>

            {/* Diagnosis */}
            {job.diagnosis && (
              <Card className="card-shadow border-purple-500/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-purple-500" />
                    Diagnosis
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={() => openWhatsApp("diagnosis")}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="whatsapp-diagnosis-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send WhatsApp
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Issue Found</h4>
                    <p>{job.diagnosis.diagnosis}</p>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Estimated Cost</h4>
                      <p className="text-xl font-bold text-primary">{formatCurrency(job.diagnosis.estimated_cost)}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Timeline</h4>
                      <p>{job.diagnosis.estimated_timeline}</p>
                    </div>
                    {job.diagnosis.parts_required && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Parts Required</h4>
                        <p>{job.diagnosis.parts_required}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Approval */}
            {job.approval && (
              <Card className="card-shadow border-green-500/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5 text-green-500" />
                    Customer Approval
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={() => openWhatsApp("approved")}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="whatsapp-approval-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send WhatsApp
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Approved By</h4>
                      <p className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {job.approval.approved_by}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Approved Amount</h4>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(job.approval.approved_amount)}</p>
                    </div>
                    {job.approval.approval_notes && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Notes</h4>
                        <p>{job.approval.approval_notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Repair */}
            {job.repair && (
              <Card className="card-shadow border-blue-500/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-blue-500" />
                    Repair Details
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={() => openWhatsApp("repaired")}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="whatsapp-repair-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send WhatsApp
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Work Done</h4>
                    <p>{job.repair.work_done}</p>
                  </div>

                  {/* Parts Used from Inventory */}
                  {job.repair.parts_used && job.repair.parts_used.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Parts Used (from Inventory)
                      </h4>
                      <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                        {job.repair.parts_used.map((part, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="font-medium">{part.item_name}</span>
                            <span className="text-muted-foreground">
                              {part.quantity} × {formatCurrency(part.unit_price || 0)} = {formatCurrency((part.quantity || 1) * (part.unit_price || 0))}
                            </span>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2 flex justify-between font-medium">
                          <span>Total Parts Cost</span>
                          <span className="text-blue-600">{formatCurrency(job.repair.parts_cost || 0)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-3 gap-4">
                    {job.repair.parts_replaced && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Other Parts</h4>
                        <p>{job.repair.parts_replaced}</p>
                      </div>
                    )}
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Final Amount</h4>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(job.repair.final_amount)}</p>
                    </div>
                    {job.repair.warranty_info && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Warranty</h4>
                        <p>{job.repair.warranty_info}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delivery */}
            {job.delivery && (
              <Card className="card-shadow border-emerald-500/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-emerald-500" />
                    Delivery Details
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={() => openWhatsApp("delivered")}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="whatsapp-delivery-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send WhatsApp
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-4 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Delivered To</h4>
                      <p className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {job.delivery.delivered_to}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Amount Received</h4>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(job.delivery.amount_received)}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Payment Mode</h4>
                      <p className="flex items-center gap-1">
                        <CreditCard className="w-4 h-4" />
                        {job.delivery.payment_mode}
                      </p>
                    </div>
                    {job.delivery.payment_reference && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Reference</h4>
                        <p className="font-mono text-sm">{job.delivery.payment_reference}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Right column */}
          <div className="space-y-6">
            {/* Actions Card */}
            {job.status !== "closed" && (
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {canDiagnose && (
                    <Button className="w-full" onClick={() => setDiagnosisModal(true)} data-testid="diagnose-btn">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Add Diagnosis
                    </Button>
                  )}
                  {canApprove && (
                    <Button className="w-full" onClick={() => setApprovalModal(true)} data-testid="approve-btn">
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Record Approval
                    </Button>
                  )}
                  {canDecline && (
                    <Button 
                      className="w-full border-red-500 text-red-600 hover:bg-red-50" 
                      variant="outline" 
                      onClick={() => setDeclineModal(true)} 
                      data-testid="decline-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Customer Declined
                    </Button>
                  )}
                  {canMarkPendingParts && (
                    <Button className="w-full" variant="outline" onClick={handlePendingParts} data-testid="pending-parts-btn">
                      <Timer className="w-4 h-4 mr-2" />
                      Mark Pending Parts
                    </Button>
                  )}
                  {canRepair && (
                    <Button className="w-full" onClick={() => setRepairModal(true)} data-testid="repair-btn">
                      <Wrench className="w-4 h-4 mr-2" />
                      {job.repair ? "Update Repair" : "Mark Repaired"}
                    </Button>
                  )}
                  {canReturnWithoutRepair && (
                    <Button 
                      className="w-full border-slate-500 text-slate-600 hover:bg-slate-50" 
                      variant="outline" 
                      onClick={() => setReturnWithoutRepairModal(true)} 
                      data-testid="return-without-repair-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Return Without Repair
                    </Button>
                  )}
                  {canDeliver && (
                    <Button className="w-full" onClick={() => setDeliveryModal(true)} data-testid="deliver-btn">
                      <Truck className="w-4 h-4 mr-2" />
                      Record Delivery
                    </Button>
                  )}
                  {canClose && (
                    <Button className="w-full" variant="default" onClick={handleClose} data-testid="close-btn">
                      <Package className="w-4 h-4 mr-2" />
                      Close Job
                    </Button>
                  )}
                  
                  {/* Inform Technician - Always visible for non-closed jobs */}
                  <Button 
                    className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" 
                    variant="outline"
                    onClick={handleOpenTechnicianModal}
                    data-testid="inform-technician-btn"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Inform Technician
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Customer Tracking */}
            {trackingLink && (
              <Card className="card-shadow border-blue-500/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <QrCode className="w-5 h-5 text-blue-500" />
                    Customer Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Share this with customer to track status.
                  </p>
                  <div className="bg-muted rounded-lg p-3 font-mono text-xs">
                    <p className="text-muted-foreground mb-1">Token:</p>
                    <p className="font-bold text-lg">{trackingLink.tracking_token}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={copyTrackingLink}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => window.open(trackingLink.tracking_path, "_blank")}>
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status Timeline */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Status Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...job.status_history].reverse().map((entry, index) => {
                    const EntryIcon = STATUS_ICONS[entry.status] || Clock;
                    return (
                      <div key={index} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(entry.status)}`}>
                            <EntryIcon className="w-4 h-4" />
                          </div>
                          {index < job.status_history.length - 1 && (
                            <div className="w-0.5 flex-1 bg-border mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium">{getStatusLabel(entry.status)}</p>
                          <p className="text-sm text-muted-foreground">{entry.notes}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(entry.timestamp)} • {entry.user_name}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Diagnosis Modal */}
      <Dialog open={diagnosisModal} onOpenChange={setDiagnosisModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Diagnosis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Issue Found *</Label>
              <div className="flex gap-2">
                <Textarea
                  value={diagnosisForm.diagnosis}
                  onChange={(e) => setDiagnosisForm({ ...diagnosisForm, diagnosis: e.target.value })}
                  placeholder="Describe the issue found..."
                  rows={3}
                  className="flex-1"
                />
                <VoiceMicButton
                  onTranscribed={(text) => {
                    setDiagnosisForm(prev => ({
                      ...prev,
                      diagnosis: prev.diagnosis ? `${prev.diagnosis}\n${text}` : text
                    }));
                  }}
                  data-testid="diagnosis-voice-btn"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Tip: Click the mic to speak in any language - it will be transcribed to English
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estimated Cost ({currencySymbol}) *</Label>
                <Input
                  type="number"
                  value={diagnosisForm.estimated_cost}
                  onChange={(e) => setDiagnosisForm({ ...diagnosisForm, estimated_cost: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Timeline *</Label>
                <Input
                  value={diagnosisForm.estimated_timeline}
                  onChange={(e) => setDiagnosisForm({ ...diagnosisForm, estimated_timeline: e.target.value })}
                  placeholder="e.g., 2-3 days"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parts Required</Label>
              <Input
                value={diagnosisForm.parts_required}
                onChange={(e) => setDiagnosisForm({ ...diagnosisForm, parts_required: e.target.value })}
                placeholder="e.g., Screen, Battery"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDiagnosisModal(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleDiagnosis(false)} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Only"}
            </Button>
            <Button onClick={() => handleDiagnosis(true)} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Send WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Modal */}
      <Dialog open={approvalModal} onOpenChange={setApprovalModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Record Customer Approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Estimated cost from diagnosis:</p>
              <p className="text-xl font-bold">{formatCurrency(job.diagnosis?.estimated_cost || 0)}</p>
            </div>
            <div className="space-y-2">
              <Label>Approved By (Customer Name) *</Label>
              <Input
                value={approvalForm.approved_by}
                onChange={(e) => setApprovalForm({ ...approvalForm, approved_by: e.target.value })}
                placeholder="Customer name who approved"
              />
            </div>
            <div className="space-y-2">
              <Label>Approved Amount ({currencySymbol}) *</Label>
              <Input
                type="number"
                value={approvalForm.approved_amount}
                onChange={(e) => setApprovalForm({ ...approvalForm, approved_amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={approvalForm.approval_notes}
                onChange={(e) => setApprovalForm({ ...approvalForm, approval_notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setApprovalModal(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleApproval(false)} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Only"}
            </Button>
            <Button onClick={() => handleApproval(true)} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Send WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Modal */}
      <Dialog open={repairModal} onOpenChange={(open) => {
        setRepairModal(open);
        if (open) {
          fetchInventoryItems();
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Repair Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Work Done *</Label>
              <div className="flex gap-2">
                <Textarea
                  value={repairForm.work_done}
                  onChange={(e) => setRepairForm({ ...repairForm, work_done: e.target.value })}
                  placeholder="Describe the repair work..."
                  rows={3}
                  data-testid="repair-work-done"
                  className="flex-1"
                />
                <VoiceMicButton
                  onTranscribed={(text) => {
                    setRepairForm(prev => ({
                      ...prev,
                      work_done: prev.work_done ? `${prev.work_done}\n${text}` : text
                    }));
                  }}
                  data-testid="repair-voice-btn"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Tip: Click the mic to speak in any language
              </p>
            </div>
            
            {/* Parts from Inventory Section */}
            <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Parts from Inventory
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowPartsSelector(!showPartsSelector);
                    if (!showPartsSelector) fetchInventoryItems();
                  }}
                  data-testid="add-parts-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Part
                </Button>
              </div>

              {/* Parts Search/Selector */}
              {showPartsSelector && (
                <div className="border rounded-lg p-3 bg-background space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search inventory..."
                      value={partsSearchQuery}
                      onChange={(e) => {
                        setPartsSearchQuery(e.target.value);
                        fetchInventoryItems(e.target.value);
                      }}
                      className="pl-10"
                      data-testid="parts-search-input"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {loadingInventory ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    ) : inventoryItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No items found
                      </p>
                    ) : (
                      inventoryItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-2 rounded hover:bg-muted cursor-pointer"
                          onClick={() => handleAddPart(item)}
                          data-testid={`inventory-item-${item.id}`}
                        >
                          <div>
                            <p className="font-medium text-sm">{item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Stock: {item.quantity} • {formatCurrency(item.selling_price)}
                            </p>
                          </div>
                          <Badge variant={item.quantity > 0 ? "secondary" : "destructive"}>
                            {item.quantity > 0 ? "In Stock" : "Out"}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Selected Parts List */}
              {selectedParts.length > 0 ? (
                <div className="space-y-2">
                  {selectedParts.map((part) => (
                    <div
                      key={part.inventory_id}
                      className="flex items-center justify-between bg-background rounded-lg p-3 border"
                      data-testid={`selected-part-${part.inventory_id}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{part.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(part.unit_price)} each
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max={part.available_stock || 999}
                          value={part.quantity}
                          onChange={(e) => handlePartQuantityChange(part.inventory_id, parseInt(e.target.value) || 1)}
                          className="w-16 text-center"
                          data-testid={`part-qty-${part.inventory_id}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleRemovePart(part.inventory_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2 text-sm">
                    <span className="text-muted-foreground">Total Parts Cost: </span>
                    <span className="font-bold text-blue-600">
                      {formatCurrency(selectedParts.reduce((sum, p) => sum + (p.quantity * p.unit_price), 0))}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      (Stock will be deducted)
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No parts selected. Click &quot;Add Part&quot; to select from inventory.
                </p>
              )}
            </div>

            {/* Legacy Text Field for Parts not in Inventory */}
            <div className="space-y-2">
              <Label>Other Parts (not in inventory)</Label>
              <Input
                value={repairForm.parts_replaced}
                onChange={(e) => setRepairForm({ ...repairForm, parts_replaced: e.target.value })}
                placeholder="e.g., Screws, Adhesive"
                data-testid="repair-parts-text"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Final Amount ({currencySymbol}) *</Label>
                <Input
                  type="number"
                  value={repairForm.final_amount}
                  onChange={(e) => setRepairForm({ ...repairForm, final_amount: e.target.value })}
                  placeholder="0"
                  data-testid="repair-final-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Warranty</Label>
                <Input
                  value={repairForm.warranty_info}
                  onChange={(e) => setRepairForm({ ...repairForm, warranty_info: e.target.value })}
                  placeholder="e.g., 3 months"
                  data-testid="repair-warranty"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setRepairModal(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleRepair(false)} disabled={actionLoading} data-testid="save-repair-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Only"}
            </Button>
            <Button onClick={() => handleRepair(true)} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Send WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Modal */}
      <Dialog open={deliveryModal} onOpenChange={setDeliveryModal}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Final amount from repair:</p>
              <p className="text-xl font-bold">{formatCurrency(job.repair?.final_amount || 0)}</p>
            </div>
            <div className="space-y-2">
              <Label>Delivered To (Name) *</Label>
              <Input
                value={deliveryForm.delivered_to}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivered_to: e.target.value })}
                placeholder="Person who received the device"
              />
            </div>
            
            {/* Credit/Payment Toggle */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Payment Type</Label>
                  <p className="text-sm text-muted-foreground">Is this a credit or full payment?</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${!deliveryForm.is_credit ? 'font-medium' : 'text-muted-foreground'}`}>Full Payment</span>
                  <Switch
                    checked={deliveryForm.is_credit}
                    onCheckedChange={(checked) => setDeliveryForm({ 
                      ...deliveryForm, 
                      is_credit: checked,
                      amount_received: checked ? "0" : deliveryForm.amount_received 
                    })}
                  />
                  <span className={`text-sm ${deliveryForm.is_credit ? 'font-medium text-orange-500' : 'text-muted-foreground'}`}>Credit</span>
                </div>
              </div>
              {deliveryForm.is_credit && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3">
                  <p className="text-sm text-orange-500">
                    <strong>Credit Mode:</strong> The full amount of {formatCurrency(job.repair?.final_amount || 0)} will be added to customer&apos;s outstanding balance.
                  </p>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount Received ({currencySymbol}) {!deliveryForm.is_credit && '*'}</Label>
                <Input
                  type="number"
                  value={deliveryForm.amount_received}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, amount_received: e.target.value })}
                  placeholder="0"
                  disabled={deliveryForm.is_credit}
                />
                {deliveryForm.is_credit && (
                  <p className="text-xs text-muted-foreground">Disabled in credit mode</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <Select
                  value={deliveryForm.payment_mode}
                  onValueChange={(v) => setDeliveryForm({ ...deliveryForm, payment_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                    <SelectItem value="Credit">Credit (Pay Later)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Reference</Label>
              <Input
                value={deliveryForm.payment_reference}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, payment_reference: e.target.value })}
                placeholder="UPI ID, Transaction ID, etc."
              />
            </div>

            {/* Expense Fields (Optional) */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-3 text-muted-foreground">
                Expense Tracking (Optional - Can add later)
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Parts/Materials Cost ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={deliveryForm.expense_parts}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, expense_parts: e.target.value })}
                    placeholder="0"
                    data-testid="expense-parts-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Labor Cost ({currencySymbol})</Label>
                  <Input
                    type="number"
                    value={deliveryForm.expense_labor}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, expense_labor: e.target.value })}
                    placeholder="0"
                    data-testid="expense-labor-input"
                  />
                </div>
              </div>
              {(deliveryForm.expense_parts || deliveryForm.expense_labor) && (
                <div className="mt-2 text-sm bg-muted/50 p-2 rounded">
                  <span className="text-muted-foreground">Estimated Profit: </span>
                  <span className="font-bold text-green-600">
                    {formatCurrency((parseFloat(deliveryForm.amount_received) || 0) - (parseFloat(deliveryForm.expense_parts) || 0) - (parseFloat(deliveryForm.expense_labor) || 0))}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={deliveryForm.delivery_notes}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_notes: e.target.value })}
                placeholder="Any delivery notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeliveryModal(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => handleDelivery(false)} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Only"}
            </Button>
            <Button onClick={() => handleDelivery(true)} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Send WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Modal */}
      <Dialog open={whatsappModal} onOpenChange={setWhatsappModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Send WhatsApp Message
            </DialogTitle>
          </DialogHeader>
          {whatsappData && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4 max-h-80 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm font-sans">{whatsappData.message}</pre>
              </div>
              <p className="text-sm text-muted-foreground">
                This will open WhatsApp with the pre-filled message.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappModal(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                window.open(whatsappData.whatsapp_url, "_blank");
                setWhatsappModal(false);
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Open WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Technician Selection Modal */}
      <Dialog open={technicianModal} onOpenChange={setTechnicianModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Inform Technician
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Select a technician to notify about this job via WhatsApp.
              Customer details will NOT be shared.
            </p>

            {loadingTechnicians ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : technicians.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No team members found</p>
                <p className="text-xs mt-1">Add technicians in Team settings</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {technicians.map((tech) => (
                  <div
                    key={tech.id}
                    onClick={() => setSelectedTechnician(tech.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTechnician === tech.id
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-border hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{tech.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {tech.phone || "No phone number"}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        tech.role === "admin" 
                          ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" 
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                      }`}>
                        {tech.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {job && selectedTechnician && (
              <div className="bg-muted/50 rounded-lg p-3 text-xs">
                <p className="font-medium mb-1">Message Preview:</p>
                <pre className="whitespace-pre-wrap text-muted-foreground max-h-32 overflow-y-auto">
                  {generateTechnicianMessage()}
                </pre>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setTechnicianModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendToTechnician}
                disabled={!selectedTechnician || !technicians.find(t => t.id === selectedTechnician)?.phone}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Send WhatsApp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Estimate Modal */}
      <Dialog open={declineModal} onOpenChange={setDeclineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customer Declined Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                This will mark the job as declined. The customer does not want to proceed with the repair.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Decline *</Label>
              <Select
                value={declineForm.decline_reason}
                onValueChange={(v) => setDeclineForm({ ...declineForm, decline_reason: v })}
              >
                <SelectTrigger data-testid="decline-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost_too_high">Cost Too High</SelectItem>
                  <SelectItem value="found_alternative">Found Alternative Solution</SelectItem>
                  <SelectItem value="no_longer_needed">No Longer Needed</SelectItem>
                  <SelectItem value="buying_new_device">Buying New Device</SelectItem>
                  <SelectItem value="time_constraints">Time Constraints</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {declineForm.decline_reason === "other" && (
              <div className="space-y-2">
                <Label>Please specify reason *</Label>
                <Input
                  value={declineForm.other_reason || ""}
                  onChange={(e) => setDeclineForm({ ...declineForm, other_reason: e.target.value })}
                  placeholder="Enter reason for decline"
                  data-testid="decline-other-reason"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={declineForm.decline_notes}
                onChange={(e) => setDeclineForm({ ...declineForm, decline_notes: e.target.value })}
                placeholder="Any additional details..."
                rows={3}
                data-testid="decline-notes"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeclineModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleDecline} 
                disabled={actionLoading || !declineForm.decline_reason || (declineForm.decline_reason === "other" && !declineForm.other_reason?.trim())}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-decline-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Mark Declined
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Without Repair Modal */}
      <Dialog open={returnWithoutRepairModal} onOpenChange={setReturnWithoutRepairModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Without Repair</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm text-slate-700">
                The device cannot be repaired and will be returned to the customer.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Return *</Label>
              <Select
                value={returnForm.return_reason}
                onValueChange={(v) => setReturnForm({ ...returnForm, return_reason: v })}
              >
                <SelectTrigger data-testid="return-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unrepairable">Device Unrepairable</SelectItem>
                  <SelectItem value="parts_unavailable">Parts Not Available</SelectItem>
                  <SelectItem value="water_damage_extensive">Extensive Water Damage</SelectItem>
                  <SelectItem value="board_damage">Motherboard/Logic Board Damage</SelectItem>
                  <SelectItem value="not_worth_repairing">Repair Cost Exceeds Device Value</SelectItem>
                  <SelectItem value="customer_changed_mind">Customer Changed Mind</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {returnForm.return_reason === "other" && (
              <div className="space-y-2">
                <Label>Please specify reason *</Label>
                <Input
                  value={returnForm.other_reason || ""}
                  onChange={(e) => setReturnForm({ ...returnForm, other_reason: e.target.value })}
                  placeholder="Enter reason for return"
                  data-testid="return-other-reason"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Diagnostic Fee (₹)</Label>
              <Input
                type="number"
                value={returnForm.diagnostic_fee}
                onChange={(e) => setReturnForm({ ...returnForm, diagnostic_fee: e.target.value })}
                placeholder="Enter diagnostic fee if applicable"
                data-testid="diagnostic-fee-input"
              />
            </div>

            {returnForm.diagnostic_fee && parseFloat(returnForm.diagnostic_fee) > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="fee_received"
                  checked={returnForm.fee_received}
                  onChange={(e) => setReturnForm({ ...returnForm, fee_received: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="fee_received">Diagnostic fee received</Label>
              </div>
            )}

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={returnForm.return_notes}
                onChange={(e) => setReturnForm({ ...returnForm, return_notes: e.target.value })}
                placeholder="Technical details, what was attempted, etc."
                rows={3}
                data-testid="return-notes"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReturnWithoutRepairModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleReturnWithoutRepair} 
                disabled={actionLoading || !returnForm.return_reason || (returnForm.return_reason === "other" && !returnForm.other_reason?.trim())}
                className="flex-1 bg-slate-600 hover:bg-slate-700"
                data-testid="confirm-return-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Confirm Return
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
